import { useState, useMemo, useEffect, useCallback } from 'react';
import { useGameStore } from '@hollowveil/shared';
import './RiddleUI.css';
import { WalkieTalkiePuzzle } from './WalkieTalkiePuzzle';
import { BogglePuzzle } from './BogglePuzzle';

export function RiddleUI() {
  const { currentRiddle, answerRiddle, closeRiddle, solveRiddle, setVisualEffect } = useGameStore();
  const [textAnswer, setTextAnswer] = useState('');
  const [selectedChoice, setSelectedChoice] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showWalkiePuzzle, setShowWalkiePuzzle] = useState(false);
  const [showBogglePuzzle, setShowBogglePuzzle] = useState(false);

  // Reset state when riddle changes
  useEffect(() => {
    if (currentRiddle) {
      setTextAnswer('');
      setSelectedChoice(null);
      setIsSubmitting(false);
      setShowWalkiePuzzle(false);
      setShowBogglePuzzle(false);
    }
  }, [currentRiddle?.id]);

  // Handle walkie-talkie puzzle success
  const handleWalkieSuccess = useCallback(() => {
    // Mark the walkie puzzle as done
    solveRiddle('erica_walkie_done');
    // Close the walkie puzzle
    setShowWalkiePuzzle(false);
    // Close the riddle UI
    closeRiddle();
  }, [solveRiddle, closeRiddle]);

  // Handle opening the walkie puzzle
  const handleOpenWalkiePuzzle = useCallback(() => {
    setShowWalkiePuzzle(true);
  }, []);

  // Handle Boggle puzzle success
  const handleBoggleSuccess = useCallback(() => {
    // Close the boggle puzzle first
    setShowBogglePuzzle(false);
    // Close the riddle UI immediately (hide the dialogue panel)
    closeRiddle();
    // Mark the boggle challenge as won (demagordon_defeated will be set by the 3D model animation)
    solveRiddle('demagordon_challenge_won');
    // Trigger the 3D explosion effect - the model will handle the animation and mark defeated when done
    setVisualEffect('demagordon_explosion');
  }, [closeRiddle, setVisualEffect, solveRiddle]);

  // Handle opening the Boggle puzzle
  const handleOpenBogglePuzzle = useCallback(() => {
    setShowBogglePuzzle(true);
  }, []);

  // Check if this is a parchment-style riddle
  const { isParchment, questionText } = useMemo(() => {
    if (!currentRiddle) return { isParchment: false, questionText: '' };
    const question = currentRiddle.question;
    if (question.startsWith('parchment:')) {
      return { isParchment: true, questionText: question.slice(10) };
    }
    return { isParchment: false, questionText: question };
  }, [currentRiddle]);

  if (!currentRiddle) {
    return null;
  }

  const handleSubmitMultipleChoice = () => {
    if (!selectedChoice || isSubmitting) return;
    setIsSubmitting(true);
    const correct = answerRiddle(selectedChoice);
    if (!correct && currentRiddle.canRetry) {
      setSelectedChoice(null);
      setTimeout(() => setIsSubmitting(false), 500);
    }
  };

  const handleSubmitText = () => {
    if (!textAnswer.trim() || isSubmitting) return;
    setIsSubmitting(true);
    const correct = answerRiddle(textAnswer);
    if (!correct && currentRiddle.canRetry) {
      setTextAnswer('');
      setTimeout(() => setIsSubmitting(false), 500);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmitText();
    }
  };

  return (
    <div className="riddle-overlay" onClick={closeRiddle}>
      <div className={`riddle-panel ${isParchment ? 'riddle-panel-parchment' : ''}`} onClick={(e) => e.stopPropagation()}>
        {/* NPC Header */}
        <div className={`riddle-header ${isParchment ? 'riddle-header-parchment' : ''}`}>
          <div className="riddle-npc-icon">{isParchment ? '📜' : '🧙'}</div>
          <div className="riddle-npc-name">{currentRiddle.npcName}</div>
          <button className="riddle-close" onClick={closeRiddle}>×</button>
        </div>

        {/* Question */}
        <div className={`riddle-content ${isParchment ? 'riddle-content-parchment' : ''}`}>
          <div className={`riddle-question ${isParchment ? 'riddle-question-parchment' : ''}`}>
            {questionText.split('\n').map((line, i) => (
              <span key={i}>
                {line}
                {i < questionText.split('\n').length - 1 && <br />}
              </span>
            ))}
          </div>

          {/* Hint if available */}
          {currentRiddle.hint && (
            <div className="riddle-hint">
              <span className="hint-icon">💡</span>
              <span>{currentRiddle.hint}</span>
            </div>
          )}

          {/* Multiple Choice */}
          {currentRiddle.type === 'multiple_choice' && currentRiddle.choices && (
            <div className="riddle-choices">
              {currentRiddle.choices.map((choice) => (
                <button
                  key={choice.id}
                  className={`riddle-choice ${selectedChoice === choice.id ? 'selected' : ''}`}
                  onClick={() => {
                    if (isSubmitting) return;
                    setSelectedChoice(choice.id);
                    // Auto-submit if this is a dialogue choice (all choices are correct)
                    const allCorrect = currentRiddle.choices?.every(c => c.isCorrect);
                    if (allCorrect) {
                      setIsSubmitting(true);
                      answerRiddle(choice.id);
                    }
                  }}
                  disabled={isSubmitting}
                >
                  <span className="choice-indicator">
                    {selectedChoice === choice.id ? '●' : '○'}
                  </span>
                  <span className="choice-text">{choice.text}</span>
                </button>
              ))}
            </div>
          )}

          {/* Text Input */}
          {currentRiddle.type === 'text_input' && (
            <div className="riddle-text-input">
              <input
                type="text"
                value={textAnswer}
                onChange={(e) => setTextAnswer(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Entrez votre réponse..."
                autoFocus
                disabled={isSubmitting}
              />
            </div>
          )}

          {/* Walkie-Talkie Puzzle Button */}
          {currentRiddle.type === 'walkie_puzzle' && (
            <div className="riddle-walkie-action">
              <button
                className="walkie-open-btn"
                onClick={handleOpenWalkiePuzzle}
              >
                📻 Ouvrir le Walkie-Talkie
              </button>
            </div>
          )}

          {/* Boggle Puzzle Button */}
          {currentRiddle.type === 'boggle_puzzle' && (
            <div className="riddle-boggle-action">
              <button
                className="boggle-open-btn"
                onClick={handleOpenBogglePuzzle}
              >
                📝 Relever le Défi !
              </button>
            </div>
          )}
        </div>

        {/* Submit Button - only for non-puzzle types */}
        {currentRiddle.type !== 'walkie_puzzle' && currentRiddle.type !== 'boggle_puzzle' && (
          <div className="riddle-footer">
            <button
              className="riddle-submit"
              onClick={
                currentRiddle.type === 'multiple_choice'
                  ? handleSubmitMultipleChoice
                  : handleSubmitText
              }
              disabled={
                isSubmitting ||
                (currentRiddle.type === 'multiple_choice' && !selectedChoice) ||
                (currentRiddle.type === 'text_input' && !textAnswer.trim())
              }
            >
              Répondre
            </button>
          </div>
        )}
      </div>

      {/* Walkie-Talkie Puzzle Modal */}
      {currentRiddle.type === 'walkie_puzzle' && currentRiddle.walkiePuzzleConfig && (
        <WalkieTalkiePuzzle
          isOpen={showWalkiePuzzle}
          onClose={() => setShowWalkiePuzzle(false)}
          onSuccess={handleWalkieSuccess}
          targetCode={currentRiddle.walkiePuzzleConfig.targetCode}
          hint={currentRiddle.walkiePuzzleConfig.hint}
          successMessage={currentRiddle.walkiePuzzleConfig.successMessage}
        />
      )}

      {/* Boggle Puzzle Modal */}
      {currentRiddle.type === 'boggle_puzzle' && currentRiddle.bogglePuzzleConfig && (
        <BogglePuzzle
          isOpen={showBogglePuzzle}
          onClose={() => setShowBogglePuzzle(false)}
          onSuccess={handleBoggleSuccess}
          targetScore={currentRiddle.bogglePuzzleConfig.targetScore}
          timeLimit={currentRiddle.bogglePuzzleConfig.timeLimit}
        />
      )}
    </div>
  );
}
